import Popover from "@mui/material/Popover";
import { useState } from "react";
import { DATE_PRESETS } from "../constants";
import { ColumnConfig, Option, WorklistFilter } from "../types";
import { FilterValue, isoDaysAgo } from "../utils";
import useLang from "Shared/hooks/useLanguage";

// "pick" lists the filterable columns; a column opens its editor.
export type PopoverMode = { kind: "pick" } | { kind: "edit"; column: ColumnConfig };

interface Props {
  anchorEl: HTMLElement | null;
  mode: PopoverMode | null;
  columns: ColumnConfig[];
  initial?: WorklistFilter;
  // undefined = still loading, null = failed
  options: Option[] | null | undefined;
  onPick: (column: ColumnConfig) => void;
  onApply: (column: ColumnConfig, value: FilterValue) => void;
  onClose: () => void;
}

const FILTER_ICONS: Record<string, string> = {
  daterange: "bi bi-calendar-range",
  datepicker: "bi bi-calendar-event",
  timerange: "bi bi-clock",
  dropdown: "bi bi-chevron-down",
  multiselect: "bi bi-ui-checks",
  text: "bi bi-search",
  exact: "bi bi-type",
  prefix: "bi bi-cursor-text",
};

const FilterPopover = ({
  anchorEl,
  mode,
  columns,
  initial,
  options,
  onPick,
  onApply,
  onClose,
}: Props) => {
  const { t } = useLang();
  return (
    <Popover
      open={!!anchorEl && !!mode}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          mt: 0.75,
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
          border: "1px solid var(--kt-gray-200)",
        },
      }}
    >
      {mode?.kind === "pick" && (
        <>
          <h6 className="vr2-popover-title">{t("Add filter")}</h6>
          <div className="vr2-popover-list">
            {columns.map((c) => (
              <button
                key={c.id}
                type="button"
                className="vr2-popover-list-item"
                onClick={() => onPick(c)}
              >
                <i className={FILTER_ICONS[c.filterType ?? ""] ?? "bi bi-funnel"}></i>
                {t(c.label)}
              </button>
            ))}
          </div>
        </>
      )}
      {mode?.kind === "edit" && (
        <FilterEditor
          key={mode.column.id}
          column={mode.column}
          initial={initial}
          options={options}
          onApply={(v) => onApply(mode.column, v)}
          onCancel={onClose}
        />
      )}
    </Popover>
  );
};

interface EditorProps {
  column: ColumnConfig;
  initial?: WorklistFilter;
  options: Option[] | null | undefined;
  onApply: (value: FilterValue) => void;
  onCancel: () => void;
}

function FilterEditor({
  column,
  initial,
  options,
  onApply,
  onCancel,
}: EditorProps) {
  const { t } = useLang();
  const type = column.filterType;
  const [v, setV] = useState(initial?.value ?? "");
  const [from, setFrom] = useState(initial?.from ?? "");
  const [to, setTo] = useState(initial?.to ?? "");
  const [vals, setVals] = useState<string[]>(initial?.values ?? []);

  const apply = () => {
    if (type === "daterange" || type === "timerange")
      onApply({ from: from || undefined, to: to || undefined });
    else if (type === "datepicker") onApply({ from, to: from });
    else if (type === "multiselect") onApply({ values: vals });
    else onApply({ value: v.trim() });
  };

  const renderOptions = () => {
    if (options === null)
      return <p className="text-danger fs-7 mb-2">{t("Could not load options.")}</p>;
    if (options === undefined)
      return <p className="text-muted fs-7 mb-2">{t("Loading options…")}</p>;
    if (type === "multiselect")
      return (
        <div className="vr2-popover-list" style={{ padding: "0 0 0.5rem" }}>
          {!options.length && (
            <span className="text-muted fs-7">{t("No options available")}</span>
          )}
          {options.map((o) => (
            <label
              key={o.value}
              className="vr2-popover-list-item"
              style={{ cursor: "pointer" }}
            >
              <input
                className="form-check-input m-0"
                type="checkbox"
                checked={vals.includes(o.value)}
                onChange={(e) =>
                  setVals((s) =>
                    e.target.checked
                      ? [...s, o.value]
                      : s.filter((x) => x !== o.value)
                  )
                }
              />
              <span>{t(o.label)}</span>
            </label>
          ))}
        </div>
      );
    return (
      <select
        className="form-select form-select-sm mb-3"
        value={v}
        onChange={(e) => setV(e.target.value)}
        autoFocus
      >
        <option value="">{t("Select…")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {t(o.label)}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div
      className="vr2-popover-editor"
      role="dialog"
      aria-label={`${t("Filter")} ${t(column.label)}`}
    >
      <h6 className="vr2-popover-editor-title">{t(column.label)}</h6>

      {type === "daterange" && (
        <>
          <div className="d-flex flex-wrap gap-1 mb-3">
            {DATE_PRESETS.map(([label, d]) => (
              <button
                key={label}
                type="button"
                className="btn btn-sm btn-light py-1 px-3"
                style={{ fontSize: "0.8rem", borderRadius: "5px" }}
                onClick={() =>
                  onApply({ from: isoDaysAgo(d - 1), to: isoDaysAgo(0) })
                }
              >
                {t(label)}
              </button>
            ))}
          </div>
          <div className="text-muted fs-8 mb-2">{t("Or a custom range")}:</div>
        </>
      )}

      {(type === "daterange" || type === "timerange" || type === "datepicker") && (
        <div className="d-flex gap-2 mb-3">
          <input
            type={type === "timerange" ? "time" : "date"}
            className="form-control form-control-sm"
            aria-label={t("From")}
            value={from}
            autoFocus
            onChange={(e) => setFrom(e.target.value)}
          />
          {type !== "datepicker" && (
            <input
              type={type === "timerange" ? "time" : "date"}
              className="form-control form-control-sm"
              aria-label={t("To")}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </div>
      )}

      {(type === "dropdown" || type === "multiselect") && renderOptions()}

      {(type === "text" || type === "exact" || type === "prefix") && (
        <>
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder={t(
              type === "text"
                ? "contains…"
                : type === "prefix"
                  ? "starts with…"
                  : "exact value"
            )}
            value={v}
            autoFocus
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply()}
          />
          {type === "text" && (
            <div className="text-warning fs-8 mb-3">
              <i className="bi bi-exclamation-triangle me-1 text-warning"></i>
              {t('"Contains" scans rows; keep a date window on for speed.')}
            </div>
          )}
        </>
      )}

      <div className="d-flex justify-content-end gap-2 pt-1">
        <button
          type="button"
          className="btn btn-sm btn-light"
          style={{ borderRadius: "5px" }}
          onClick={onCancel}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          style={{ borderRadius: "5px" }}
          onClick={apply}
        >
          {t("Apply")}
        </button>
      </div>
    </div>
  );
}

export default FilterPopover;
